/**
 * validateMappingRules.js
 *
 * Structural validation for an AutomationBinding's `rules[]` (the
 * 'mapping' strategy's model) — purely local, static analysis of the
 * rule list itself. Does not evaluate a value (see evaluateMappingRules.js
 * for that — a separate utility, deliberately not combined with this one:
 * validation checks the rule *set*, evaluation checks one *value* against
 * it) and never modifies the rules it's given.
 *
 * Extensible by design: each check is an independent, single-purpose
 * function of `(rules, context)` returning ValidationIssue[]; CHECKS is the
 * only place a new check needs to be registered. `context` carries the few
 * facts that are not readable off `rules` (see validateMappingRules below);
 * a check that doesn't need it simply ignores it. Candidates for future checks
 * (not implemented here): overlapping ranges, unreachable numeric range,
 * enum completeness (every declared enum value has a rule), color
 * interpolation warnings.
 *
 * @typedef {{
 *   code: string,
 *   severity: 'error'|'warning',
 *   message: string,
 *   ruleIndex: number,
 *   relatedIndex?: number,
 * }} ValidationIssue
 */

import { INCOMING_VALUE_TOKEN, hasIncomingValueToken } from './incomingValueToken.js';

const SUPPORTED_OPERATORS = ['==', '!=', '<', '<=', '>', '>='];

/** @private — more than one `when: null` rule; only the first one encountered can ever apply. */
function checkMultipleDefaults(rules) {
  const defaultIndexes = rules
    .map((r, i) => (r.when === null ? i : -1))
    .filter(i => i !== -1);

  if (defaultIndexes.length <= 1) return [];

  return defaultIndexes.map(ruleIndex => ({
    code: 'multiple-defaults',
    severity: 'error',
    ruleIndex,
    message: 'Multiple default rules found — only one default rule is allowed.',
  }));
}

/** @private — a default rule that isn't the last entry. */
function checkDefaultNotLast(rules) {
  const lastIndex = rules.length - 1;
  return rules
    .map((r, i) => (r.when === null && i !== lastIndex ? i : -1))
    .filter(i => i !== -1)
    .map(ruleIndex => ({
      code: 'default-not-last',
      severity: 'error',
      ruleIndex,
      message: 'The default rule must be the last rule.',
    }));
}

/** @private — any rule after the first default rule can never be reached. */
function checkUnreachableAfterDefault(rules) {
  const firstDefault = rules.findIndex(r => r.when === null);
  if (firstDefault === -1) return [];

  return rules
    .map((_, i) => i)
    .filter(i => i > firstDefault)
    .map(ruleIndex => ({
      code: 'unreachable-after-default',
      severity: 'warning',
      ruleIndex,
      relatedIndex: firstDefault,
      message: 'This rule is unreachable — a default rule above it always matches first.',
    }));
}

/** @private — a conditioned rule with no comparison value. */
function checkEmptyComparisonValue(rules) {
  return rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.when && (r.when.value === '' || r.when.value === undefined || r.when.value === null))
    .map(({ i }) => ({
      code: 'empty-comparison-value',
      severity: 'warning',
      ruleIndex: i,
      message: 'This rule has no comparison value.',
    }));
}

/** @private — two or more '==' rules comparing against the same value; only the first can ever match. */
function checkDuplicateEquality(rules) {
  const firstSeenAt = new Map(); // comparison value -> first rule index
  const issues = [];

  rules.forEach((r, i) => {
    if (!r.when || r.when.operator !== '==') return;
    const key = String(r.when.value);
    if (firstSeenAt.has(key)) {
      issues.push({
        code: 'duplicate-equality',
        severity: 'warning',
        ruleIndex: i,
        relatedIndex: firstSeenAt.get(key),
        message: 'Duplicate condition — matches the same value as an earlier rule; this rule can never be reached.',
      });
    } else {
      firstSeenAt.set(key, i);
    }
  });

  return issues;
}

/** @private — an operator outside the supported set (bindingGenerators.js only knows these six). */
function checkInvalidOperator(rules) {
  return rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.when && !SUPPORTED_OPERATORS.includes(r.when.operator))
    .map(({ r, i }) => ({
      code: 'invalid-operator',
      severity: 'error',
      ruleIndex: i,
      message: `"${r.when.operator}" is not a supported operator.`,
    }));
}

/** @private — a rule with no result value. */
function checkMissingResult(rules) {
  return rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.result === '' || r.result === undefined || r.result === null)
    .map(({ i }) => ({
      code: 'missing-result',
      severity: 'warning',
      ruleIndex: i,
      message: 'This rule has no result value.',
    }));
}

/**
 * @private — a result still holding the editor's `{{value}}` alias.
 *
 * The alias is a display form only: the result fields translate it to the
 * binding's real Jinja expression as it is typed (see incomingValueToken.js).
 * That translation needs an expression, so a result composed before an entity
 * was selected can keep the bare alias — which Jinja renders as nothing.
 * Only reported when the caller says an expression is now available, since
 * without one there is nothing the designer could have done differently yet.
 */
function checkUnresolvedIncomingValue(rules, { hasIncomingValue } = {}) {
  if (!hasIncomingValue) return [];
  return rules
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => hasIncomingValueToken(r.result))
    .map(({ i }) => ({
      code: 'unresolved-incoming-value',
      severity: 'warning',
      ruleIndex: i,
      message: `"${INCOMING_VALUE_TOKEN}" in this result is not bound to the entity — retype it, or reinsert it from the String Builder.`,
    }));
}

// Registry — add a new check function here (and to this array) to extend
// validation; nothing else in this module or its callers needs to change.
const CHECKS = [
  checkMultipleDefaults,
  checkDefaultNotLast,
  checkUnreachableAfterDefault,
  checkEmptyComparisonValue,
  checkDuplicateEquality,
  checkInvalidOperator,
  checkMissingResult,
  checkUnresolvedIncomingValue,
];

/**
 * Validate a rules[] list. Read-only — never mutates `rules`.
 *
 * @param {Array<{when: {operator: string, value: string}|null, result: string}>} [rules]
 * @param {{ hasIncomingValue?: boolean }} [context] — optional facts a check
 *        cannot read off `rules` alone. Every check must work without it, so
 *        existing callers keep their exact results.
 * @returns {ValidationIssue[]}
 */
export function validateMappingRules(rules, context = {}) {
  const safeRules = rules ?? [];
  return CHECKS.flatMap(check => check(safeRules, context));
}
